import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, Sparkles, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { WorkspaceNav } from "@/components/WorkspaceNav";
import { TermDrawer } from "@/components/TermDrawer";
import { SensitivityBadge, LifecycleBadge } from "@/components/GlossaryBadges";
import { GLOSSARY } from "@/constants/testIds";
import { useWorkspace } from "@/context/WorkspaceContext";
import api from "@/lib/api";

const KINDS = ["table", "column", "business"];
const LIFECYCLES = ["Draft", "Active", "Deprecated"];
const SENSITIVITIES = ["Public", "Internal", "Confidential", "Restricted"];

function FilterSelect({ value, onChange, options, placeholder, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="h-9 w-[150px] text-xs">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All {placeholder}</SelectItem>
        {options.map((o) => (
          <SelectItem key={o} value={o}>
            {o}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function Glossary() {
  const { active } = useWorkspace();
  const [terms, setTerms] = useState(null);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("all");
  const [lifecycle, setLifecycle] = useState("all");
  const [sensitivity, setSensitivity] = useState("all");
  const [semantic, setSemantic] = useState("");
  const [semanticMode, setSemanticMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkLifecycle, setBulkLifecycle] = useState("");
  const [bulkSensitivity, setBulkSensitivity] = useState("");
  const [drawerTerm, setDrawerTerm] = useState(null);
  const [sampleLookup, setSampleLookup] = useState({});
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);

  const loadStats = useCallback(async () => {
    if (!active) return;
    const { data } = await api.get("/glossary/stats", { params: { workspace_id: active.id } });
    setStats(data);
  }, [active]);

  const loadSchemaLookup = useCallback(async () => {
    if (!active) return;
    const { data } = await api.get("/schema", { params: { workspace_id: active.id } });
    const map = {};
    for (const t of data) {
      for (const c of t.columns) {
        map[`${t.table_fqn}|${c.name}`] = { sample_values: c.sample_values, data_type: c.data_type };
      }
    }
    setSampleLookup(map);
  }, [active]);

  const load = useCallback(async () => {
    if (!active) return;
    const params = { workspace_id: active.id };
    if (q) params.q = q;
    if (kind !== "all") params.kind = kind;
    if (lifecycle !== "all") params.lifecycle = lifecycle;
    if (sensitivity !== "all") params.sensitivity = sensitivity;
    const { data } = await api.get("/glossary", { params });
    setTerms(data);
    setSemanticMode(false);
    setSelected(new Set());
  }, [active, q, kind, lifecycle, sensitivity]);

  useEffect(() => {
    loadStats();
    loadSchemaLookup();
  }, [loadStats, loadSchemaLookup]);

  useEffect(() => {
    load();
  }, [load]);

  const runSemantic = async (e) => {
    e?.preventDefault();
    if (!semantic.trim()) return;
    const { data } = await api.post(
      "/glossary/search",
      { query: semantic, k: 20 },
      { params: { workspace_id: active.id } }
    );
    setTerms(data);
    setSemanticMode(true);
    setSelected(new Set());
  };

  const clearSemantic = () => {
    setSemantic("");
    load();
  };

  const pollJob = async (jobId) => {
    for (let i = 0; i < 120; i++) {
      const { data } = await api.get(`/jobs/${jobId}`);
      setProgress(data.progress);
      if (data.status === "completed") return;
      if (data.status === "failed") throw new Error(data.error || "Generation failed");
      await new Promise((r) => setTimeout(r, 2000));
    }
  };

  const onGenerate = async () => {
    setGenerating(true);
    setProgress(0);
    try {
      const srcs = await api.get("/sources", { params: { workspace_id: active.id } });
      const src = srcs.data[0];
      if (!src) {
        toast.error("Connect a source on the Schema page first");
        return;
      }
      const { data } = await api.post("/glossary/generate", null, { params: { source_id: src.id } });
      await pollJob(data.job_id);
      await Promise.all([load(), loadStats(), loadSchemaLookup()]);
      toast.success("Glossary generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Failed");
    } finally {
      setGenerating(false);
    }
  };

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const applyBulk = async () => {
    const payload = { ids: [...selected] };
    if (bulkLifecycle) payload.lifecycle = bulkLifecycle;
    if (bulkSensitivity) payload.sensitivity = bulkSensitivity;
    if (!bulkLifecycle && !bulkSensitivity) {
      toast.error("Choose a lifecycle or sensitivity to apply");
      return;
    }
    const { data } = await api.post("/glossary/bulk", payload);
    toast.success(`Updated ${data.modified} terms`);
    setBulkLifecycle("");
    setBulkSensitivity("");
    await load();
  };

  const onSaved = async () => {
    setDrawerTerm(null);
    await Promise.all([load(), loadStats()]);
  };

  const isEmpty = terms && terms.length === 0 && !semanticMode && !q && kind === "all" && lifecycle === "all" && sensitivity === "all";
  const drawerSample = useMemo(() => {
    if (!drawerTerm || drawerTerm.kind !== "column") return { sample_values: [], data_type: null };
    return sampleLookup[`${drawerTerm.table_fqn}|${drawerTerm.column_name}`] || { sample_values: [], data_type: null };
  }, [drawerTerm, sampleLookup]);

  return (
    <div data-testid={GLOSSARY.root} className="min-h-screen bg-background text-foreground">
      <WorkspaceNav />
      <main className="mx-auto w-full max-w-7xl px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Glossary</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              A living, editable dictionary of what every table and column means.
            </p>
          </div>
          {stats && stats.total_tables > 0 && (
            <div data-testid={GLOSSARY.coverage} className="text-right text-xs text-muted-foreground">
              <div>
                Tables <span className="text-accent">{Math.round(stats.table_coverage * 100)}%</span> ·{" "}
                Columns <span className="text-accent">{Math.round(stats.column_coverage * 100)}%</span> defined
              </div>
              <div className="mt-0.5">
                {stats.tables_defined}/{stats.total_tables} tables · {stats.columns_defined}/{stats.total_columns} columns
              </div>
            </div>
          )}
        </div>

        {isEmpty ? (
          <div
            data-testid={GLOSSARY.empty}
            className="mt-12 flex flex-col items-center rounded-2xl border border-dashed border-border/60 bg-card/40 px-6 py-16 text-center"
          >
            <Sparkles className="h-8 w-8 text-accent" />
            <h2 className="mt-5 text-lg font-semibold">No definitions yet</h2>
            <p className="mt-2 max-w-sm text-sm text-muted-foreground">
              Generate audit-grade definitions for every table and column in your
              connected schema.
            </p>
            {generating && (
              <div className="mt-6 w-full max-w-sm">
                <Progress value={progress} className="h-2" />
                <p className="mt-2 text-xs text-muted-foreground">Generating… {progress}%</p>
              </div>
            )}
            <Button
              onClick={onGenerate}
              disabled={generating}
              data-testid={GLOSSARY.generateButton}
              className="mt-7 rounded-full bg-accent text-xs uppercase tracking-[0.18em] text-accent-foreground hover:bg-accent/90"
            >
              {generating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
              Run glossary generation
            </Button>
          </div>
        ) : (
          <>
            {/* Search + filters */}
            <div className="mt-8 space-y-3">
              <form onSubmit={runSemantic} className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    data-testid={GLOSSARY.searchInput}
                    value={semantic}
                    onChange={(e) => setSemantic(e.target.value)}
                    placeholder="Semantic search — e.g. monthly revenue, PII columns, readmission rate"
                    className="h-10 bg-card/60 pl-9"
                  />
                </div>
                <Button
                  type="submit"
                  data-testid={GLOSSARY.searchButton}
                  className="h-10 rounded-md bg-accent px-5 text-xs uppercase tracking-[0.16em] text-accent-foreground hover:bg-accent/90"
                >
                  Search
                </Button>
                {semanticMode && (
                  <Button type="button" variant="outline" onClick={clearSemantic} className="h-10">
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </form>

              <div className="flex flex-wrap items-center gap-2">
                <div className="relative w-[220px]">
                  <Input
                    data-testid={GLOSSARY.textFilter}
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Filter by name…"
                    className="h-9 bg-card/60 text-xs"
                  />
                </div>
                <FilterSelect value={kind} onChange={setKind} options={KINDS} placeholder="kinds" testid={GLOSSARY.kindFilter} />
                <FilterSelect value={lifecycle} onChange={setLifecycle} options={LIFECYCLES} placeholder="lifecycles" testid={GLOSSARY.lifecycleFilter} />
                <FilterSelect value={sensitivity} onChange={setSensitivity} options={SENSITIVITIES} placeholder="sensitivities" testid={GLOSSARY.sensitivityFilter} />
              </div>
            </div>

            {/* Bulk bar */}
            {selected.size > 0 && (
              <div
                data-testid={GLOSSARY.bulkBar}
                className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-accent/40 bg-accent/5 px-4 py-3"
              >
                <span className="text-xs text-muted-foreground">{selected.size} selected</span>
                <Select value={bulkLifecycle} onValueChange={setBulkLifecycle}>
                  <SelectTrigger data-testid={GLOSSARY.bulkLifecycle} className="h-8 w-[150px] text-xs">
                    <SelectValue placeholder="Set lifecycle" />
                  </SelectTrigger>
                  <SelectContent>
                    {LIFECYCLES.map((l) => (
                      <SelectItem key={l} value={l}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={bulkSensitivity} onValueChange={setBulkSensitivity}>
                  <SelectTrigger data-testid={GLOSSARY.bulkSensitivity} className="h-8 w-[150px] text-xs">
                    <SelectValue placeholder="Set sensitivity" />
                  </SelectTrigger>
                  <SelectContent>
                    {SENSITIVITIES.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  onClick={applyBulk}
                  data-testid={GLOSSARY.bulkApply}
                  size="sm"
                  className="h-8 rounded-full bg-accent text-xs uppercase tracking-[0.14em] text-accent-foreground hover:bg-accent/90"
                >
                  Apply
                </Button>
                <Button variant="ghost" size="sm" className="h-8" onClick={() => setSelected(new Set())}>
                  Clear
                </Button>
              </div>
            )}

            {/* List */}
            <div className="mt-5 overflow-hidden rounded-xl border border-border/60">
              {terms === null ? (
                <div className="flex justify-center py-16">
                  <Loader2 className="h-5 w-5 animate-spin text-accent" />
                </div>
              ) : terms.length === 0 ? (
                <p className="py-16 text-center text-sm text-muted-foreground">No terms match your filters.</p>
              ) : (
                terms.map((t) => (
                  <div
                    key={t.id}
                    data-testid={GLOSSARY.row}
                    className="flex items-start gap-3 border-b border-border/40 px-4 py-3 transition-colors last:border-0 hover:bg-muted/30"
                  >
                    <Checkbox
                      data-testid={GLOSSARY.rowCheckbox}
                      checked={selected.has(t.id)}
                      onCheckedChange={() => toggle(t.id)}
                      className="mt-1"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <button
                      type="button"
                      data-testid="glossary-row-open"
                      onClick={() => setDrawerTerm(t)}
                      className="flex flex-1 flex-col items-start gap-1 text-left"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold">{t.display_name}</span>
                        <span className="rounded border border-border/50 px-1.5 py-0 text-[10px] uppercase text-muted-foreground">
                          {t.kind}
                        </span>
                        <SensitivityBadge value={t.sensitivity} />
                        <LifecycleBadge value={t.lifecycle} />
                        {typeof t.score === "number" && (
                          <span className="text-[10px] text-accent">match {t.score.toFixed(2)}</span>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground">{t.definition}</span>
                      <span className="text-[10px] text-muted-foreground/70">
                        {t.kind === "business"
                          ? `${t.pack_slug ? `${t.pack_slug} pack` : "Business term"}${t.domain ? ` · ${t.domain}` : ""}`
                          : `${t.table_fqn}${t.column_name ? ` · ${t.column_name}` : ""}`}
                      </span>
                      {Array.isArray(t.match) && t.match.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1" data-testid="glossary-row-reasons">
                          {t.match.map((m, i) => (
                            <span key={i} className="rounded-full border border-accent/30 bg-accent/5 px-1.5 py-0 text-[9px] text-accent">{m}</span>
                          ))}
                        </div>
                      )}
                    </button>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </main>

      <TermDrawer
        term={drawerTerm}
        sampleValues={drawerSample.sample_values}
        dataType={drawerSample.data_type}
        onOpenChange={() => setDrawerTerm(null)}
        onSaved={onSaved}
      />
    </div>
  );
}
